import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { toast } from "sonner";
import { type AxiosError } from "axios";
import { LockKeyIcon } from "@phosphor-icons/react";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { useChangePassword } from "../services/useChangePassword";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import {
  ChangePasswordDialog,
  type ChangePasswordResult,
  type ChangePasswordValues
} from "./blocks/ChangePasswordDialog";
import { SettingsSection } from "./blocks/SettingsLayout";

const NewPasswordSchema = z.string().min(8).max(32).regex(/[0-9]/).regex(/[A-Z]/);

const passwordsMatch = (form: ChangePasswordValues) =>
  form.newPassword.length > 0 && form.newPassword === form.confirmPassword;

export function ChangePasswordSection() {
  const { t } = useTranslation(["settings", "common"]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { mutateAsync: changePassword } = useChangePassword();
  const refetch = useMeStore((s) => s.refetch);

  const isValid = (form: ChangePasswordValues) =>
    form.currentPassword.length > 0 && NewPasswordSchema.safeParse(form.newPassword).success && passwordsMatch(form);

  async function handleSubmit(form: ChangePasswordValues): Promise<ChangePasswordResult> {
    try {
      await changePassword({ currentPassword: form.currentPassword, newPassword: form.newPassword });
    } catch (err) {
      const message = (err as AxiosError<{ message: string }>)?.response?.data?.message ?? t("password.changeFailed");
      toast.error(message);
      return { ok: false, error: message };
    }
    toast.success(t("password.changed"));
    await refetch?.();
    return { ok: true };
  }

  return (
    <>
      <SettingsSection
        title={t("redesign.password.title")}
        actions={
          <Button
            variant="secondary"
            shape="pill"
            data-testid="change-password-open-button"
            onClick={() => setIsDialogOpen(true)}
          >
            <LockKeyIcon data-icon="inline-start" />
            {t("redesign.password.save")}
          </Button>
        }
      />
      <ChangePasswordDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        canSubmit={isValid}
        requirements={(form) => [
          { id: "length", label: t("password.minLength"), met: form.newPassword.length >= 8 },
          { id: "number", label: t("password.hasNumber"), met: /[0-9]/.test(form.newPassword) },
          { id: "capital", label: t("password.hasCapital"), met: /[A-Z]/.test(form.newPassword) },
          { id: "match", label: t("password.matches"), met: passwordsMatch(form) }
        ]}
        onSubmit={handleSubmit}
        labels={{
          title: t("redesign.password.save"),
          description: t("redesign.password.dialogDescription"),
          current: t("redesign.password.current"),
          new: t("redesign.password.new"),
          confirm: t("redesign.password.confirm"),
          show: t("redesign.password.show"),
          hide: t("redesign.password.hide"),
          met: t("redesign.password.met"),
          notMet: t("redesign.password.notMet"),
          submit: t("redesign.password.save"),
          cancel: t("common.cancel"),
          close: t("common:close"),
          loading: t("common:loading")
        }}
      />
    </>
  );
}
